import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { WhatsAppCare } from "@/components/WhatsAppCare";
import { AskAIFloating } from "@/components/AskAIFloating";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { isInternal, CC_ROLES, PERMS, INTERNAL, OPS_ROLES } from "@/lib/constants";
import Inbox from "@/pages/Inbox";
import InboxAnalytics from "@/pages/InboxAnalytics";
import AppShell from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import PendingAccess from "@/pages/PendingAccess";
import Dashboard from "@/pages/Dashboard";
import JobOrders from "@/pages/JobOrders";
import Candidates from "@/pages/Candidates";
import CandidateForm from "@/pages/CandidateForm";
import CandidateDetail from "@/pages/CandidateDetail";
import CVBuilder from "@/pages/CVBuilder";
import ChangePassword from "@/pages/ChangePassword";
import MatchQueue from "@/pages/MatchQueue";
import Pipeline from "@/pages/Pipeline";
import Notifications from "@/pages/Notifications";
import MasterData from "@/pages/MasterData";
import Commercial from "@/pages/Commercial";
import Agreements from "@/pages/Agreements";
import Heatmap from "@/pages/Heatmap";
import AuditLogs from "@/pages/AuditLogs";
import Scorecard from "@/pages/Scorecard";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Guide from "@/pages/Guide";
import MobileManagement from "@/pages/MobileManagement";
import Alumni from "@/pages/Alumni";

const Loading = () => <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">Memuat...</div>;

function Protected({ children, internalOnly, roles }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (user.role === "pending") return <PendingAccess />;
  if (user.must_change_password) return <ChangePassword forced />;
  if (internalOnly && !isInternal(user.role)) return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loading />;
  if (user) return <Navigate to={location.state?.from || "/"} replace />;
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (user?.role === "customer_care") return <Navigate to="/inbox" replace />;
  return <Dashboard />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/" element={<RoleHome />} />
        <Route path="/inbox" element={<Protected roles={CC_ROLES}><Inbox /></Protected>} />
        <Route path="/inbox/analytics" element={<Protected roles={CC_ROLES}><InboxAnalytics /></Protected>} />
        <Route path="/job-orders" element={<JobOrders />} />
        <Route path="/candidates" element={<Candidates />} />
        <Route path="/candidates/new" element={<CandidateForm />} />
        <Route path="/candidates/:id/edit" element={<CandidateForm />} />
        <Route path="/candidates/:id" element={<CandidateDetail />} />
        <Route path="/candidates/:id/cv-builder" element={<CVBuilder />} />
        <Route path="/cv-maker" element={<Protected roles={PERMS.cv_maker}><CVBuilder /></Protected>} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/queue" element={<Protected roles={PERMS.queue}><MatchQueue /></Protected>} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/commercial" element={<Protected roles={PERMS.money}><Commercial /></Protected>} />
        <Route path="/agreements" element={<Protected roles={PERMS.agreements_view}><Agreements /></Protected>} />
        <Route path="/alumni" element={<Protected roles={OPS_ROLES}><Alumni /></Protected>} />
        <Route path="/heatmap" element={<Heatmap />} />
        <Route path="/guide" element={<Guide />} />
        <Route path="/scorecard" element={<Protected roles={[...INTERNAL, "vendor_leader", "vendor_operator"]}><Scorecard /></Protected>} />
        <Route path="/mobile" element={<Protected roles={PERMS.mobile}><MobileManagement /></Protected>} />
        <Route path="/master" element={<Protected roles={[...new Set([...PERMS.master, ...PERMS.users, ...PERMS.branding, ...PERMS.whatsapp_config, ...PERMS.vendor_write])]}><MasterData /></Protected>} />
        <Route path="/audit" element={<Protected roles={PERMS.audit}><AuditLogs /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <WhatsAppCare />
        <AskAIFloating />
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
